/**
 * Agent coverage arithmetic. PURE — extracted from AiUsageDashboard's
 * AwarenessStrip so the weighting is tested instead of buried in a render.
 */

import type { ToolCatalogModule } from '../../../services/aiControlApi';

const SIX_HOURS = 6 * 3600 * 1000;
const SEVEN_DAYS = 7 * 24 * 3600 * 1000;

export function endpointPct(totalEndpoints: number, totalGaps: number): number {
  if (!totalEndpoints) return 0;
  return Math.round(((totalEndpoints - totalGaps) / totalEndpoints) * 100);
}

export function modulePct(
  modules: ToolCatalogModule[],
): { pct: number; covered: number; scorable: number } {
  // 'skipped' modules are meta (no backend domains) and would drag the score
  // down for something that can never be covered.
  const scorable = modules.filter((m) => m.status !== 'skipped');
  const covered = scorable.filter((m) => m.status === 'full' || m.status === 'partial');
  return {
    pct: scorable.length ? Math.round((covered.length / scorable.length) * 100) : 0,
    covered: covered.length,
    scorable: scorable.length,
  };
}

/** 100 inside six hours, degrading linearly to 0 at seven days. */
export function freshnessPct(ageMs: number): number {
  if (!Number.isFinite(ageMs)) return 0;
  if (ageMs < SIX_HOURS) return 100;
  if (ageMs >= SEVEN_DAYS) return 0;
  return Math.round(100 * (1 - (ageMs - SIX_HOURS) / (SEVEN_DAYS - SIX_HOURS)));
}

/** endpoints 40% · modules 25% · entities 20% · freshness 15%. */
export function awarenessScore(input: {
  endpointPct: number; modulePct: number; entityPct: number; freshnessPct: number;
}): number {
  return Math.round(
    input.endpointPct * 0.40 +
    input.modulePct * 0.25 +
    input.entityPct * 0.20 +
    input.freshnessPct * 0.15,
  );
}

export function formatRelative(ageMs: number): string {
  if (!Number.isFinite(ageMs)) return 'never';
  const mins = Math.floor(ageMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function domainStatus(
  tools: Array<{ coverage: string }>,
): 'none' | 'full' | 'partial' | 'stub' {
  if (tools.length === 0) return 'none';
  const anyFull = tools.some((t) => t.coverage === 'full');
  const anyPartial = tools.some((t) => t.coverage === 'partial');
  const anyStub = tools.some((t) => t.coverage === 'stub');
  if (anyFull && !anyStub && !anyPartial) return 'full';
  if (anyStub && !anyFull) return 'stub';
  return 'partial';
}

export function awarenessColor(pct: number): string {
  if (pct >= 70) return 'text-emerald-600';
  if (pct >= 40) return 'text-amber-600';
  return 'text-rose-600';
}
